import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CheckCircle2, Inbox, Loader2, Trash2, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { TODO_STATUS } from "../api/adapters";
import { useTodoStore } from "../store/todoStore";
import { TodoItem } from "./TodoItem";

type Props = {
  disabled?: boolean;
  onOpenTodo?: (id: string) => void;
};

export function TodoList({ disabled = false, onOpenTodo }: Props) {
  const {
    filteredTodos,
    reorderTodos,
    toggleTodoAsync,
    deleteTodoAsync,
    filter,
    isLoading,
    selectedIds,
    toggleSelectedTodo,
    selectAllVisibleTodos,
    clearSelectedTodos,
    bulkUpdateStatus,
    bulkDeleteSelected,
  } = useTodoStore();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const todos = filteredTodos();
      const oldIndex = todos.findIndex((todo) => todo.id === active.id);
      const newIndex = todos.findIndex((todo) => todo.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        reorderTodos(oldIndex, newIndex);
      }
    }
  };

  const handleToggle = async (id: string) => {
    try {
      await toggleTodoAsync(id);
    } catch (_error) {
      // 错误已在 API 层处理
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteTodoAsync(id);
    } catch (_error) {
      // 错误已在 API 层处理
    }
  };

  const handleBulkStatus = async (status: "inProgress" | "completed" | "archived") => {
    try {
      await bulkUpdateStatus(status);
    } catch (_error) {
      // 错误已在 API 层处理
    }
  };

  const handleBulkDelete = async () => {
    try {
      await bulkDeleteSelected();
    } catch (_error) {
      // 错误已在 API 层处理
    }
  };

  const todos = filteredTodos();
  const isEmpty = todos.length === 0;
  const selectedCount = selectedIds.length;
  const visibleSelectedCount = todos.filter((todo) => selectedIds.includes(todo.id)).length;

  if (isLoading && isEmpty) {
    return (
      <output className="space-y-3" aria-label="任务列表加载中">
        {[0, 1, 2].map((item) => (
          <div key={item} className="h-20 animate-pulse rounded-md border border-border bg-card" />
        ))}
      </output>
    );
  }

  return (
    <div className="space-y-3">
      {isEmpty ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center justify-center py-12 text-gray-400 dark:text-gray-500"
        >
          <Inbox size={48} className="mb-4" />
          <p className="text-lg font-medium">
            {filter.status === "completed"
              ? "没有已完成的任务"
              : filter.status === "active"
                ? "没有进行中的任务"
                : "还没有任务"}
          </p>
          <p className="text-sm">创建你的第一个任务吧</p>
        </motion.div>
      ) : (
        <>
          {selectedCount > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-card px-3 py-2 shadow-sm">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={visibleSelectedCount === todos.length}
                  onChange={selectAllVisibleTodos}
                  aria-label="选择当前列表全部任务"
                  className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                />
                <span className="text-sm text-muted-foreground">已选择 {selectedCount} 项</span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  disabled={disabled || isLoading}
                  onClick={() => handleBulkStatus(TODO_STATUS.inProgress)}
                  className="inline-flex h-8 items-center gap-1 rounded-md bg-secondary px-3 text-sm text-secondary-foreground hover:bg-secondary/80 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isLoading ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                  进行中
                </button>
                <button
                  type="button"
                  disabled={disabled || isLoading}
                  onClick={() => handleBulkStatus(TODO_STATUS.completed)}
                  className="inline-flex h-8 items-center gap-1 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isLoading ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                  完成
                </button>
                <button
                  type="button"
                  disabled={disabled || isLoading}
                  onClick={handleBulkDelete}
                  className="inline-flex h-8 items-center gap-1 rounded-md bg-destructive px-3 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Trash2 size={14} />
                  删除
                </button>
                <button
                  type="button"
                  disabled={disabled || isLoading}
                  onClick={clearSelectedTodos}
                  className="inline-flex h-8 items-center justify-center rounded-md px-2 text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label="清除选择"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          )}

          {selectedCount === 0 && (
            <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 shadow-sm">
              <input
                type="checkbox"
                checked={false}
                onChange={selectAllVisibleTodos}
                aria-label="选择当前列表全部任务"
                className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
              />
              <span className="text-sm text-muted-foreground">批量选择</span>
            </div>
          )}

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={todos.map((t) => t.id)} strategy={verticalListSortingStrategy}>
              <AnimatePresence mode="popLayout">
                {todos.map((todo) => (
                  <TodoItem
                    key={todo.id}
                    todo={todo}
                    disabled={disabled}
                    selected={selectedIds.includes(todo.id)}
                    onSelect={toggleSelectedTodo}
                    onToggle={handleToggle}
                    onDelete={handleDelete}
                    onOpen={onOpenTodo}
                  />
                ))}
              </AnimatePresence>
            </SortableContext>
          </DndContext>
        </>
      )}
    </div>
  );
}
